import { LoadingIndicator } from './LoadingIndicator';
import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { useAbortController } from '../hooks/useAbortController';
import type {
  ColDef,
  IDatasource,
  IGetRowsParams,
  GetRowIdParams,
  GridReadyEvent,
  CellMouseDownEvent,
  RowClickedEvent,
  FilterChangedEvent,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { useApiFetch } from '../hooks/useApiFetch';
import { useGridLayout } from '../hooks/useGridLayout';
import type { SampleListItem, SampleStatus } from '../types/samples';
import { STATUS_OPTIONS } from '../types/samples';
import { ErrorDisplay } from './ErrorDisplay';
import { RefreshError } from './RefreshError';
import { ListSkeleton } from './ListSkeleton';
import { PageHeader } from './PageHeader';
import {
  TimeAgoCellRenderer,
  NumberCellRenderer,
  DurationCellRenderer,
  CopyButtonCellRenderer,
  CopyButton,
  TimelineLinkCellRenderer,
} from './ag-grid/cellRenderers';
import { isCopyButtonClick } from './ag-grid/isCopyButtonClick';
import { isLinkClick } from './ag-grid/isLinkClick';
import { ResetGridLayoutButton } from './ag-grid/ResetGridLayoutButton';
import './ag-grid/styles.css';
import { isButtonClick } from './ag-grid/isButtonClick';
import { OpenInViewerMenu } from './OpenInMenu';
import { useViewers } from '../hooks/useViewers';
import { openViewerUrl, type ViewerTarget } from '../utils/viewers';
import { localInputToIso, isoToLocalInput } from '../utils/datetime';
import { formatScores, formatScoresFull } from '../utils/formatScores';

ModuleRegistry.registerModules([AllCommunityModule]);

const PAGE_SIZE = 100;

// Maps AG Grid field names to backend filter_* query param names
const COLUMN_FILTER_PARAMS: Record<string, string> = {
  model: 'filter_model',
  created_by: 'filter_created_by',
  task_name: 'filter_task_name',
  task_version: 'filter_task_version',
  eval_set_id: 'filter_eval_set_id',
  error_message: 'filter_error_message',
  id: 'filter_id',
};

function sampleTarget(sample: SampleListItem): ViewerTarget {
  return {
    level: 'sample',
    evalSetId: sample.eval_set_id,
    logFile: sample.filename,
    sampleId: sample.id,
    epoch: sample.epoch,
    sampleUuid: sample.uuid,
  };
}

function OpenInCellRenderer({ data }: { data?: SampleListItem }) {
  if (!data) return null;
  return <OpenInViewerMenu target={sampleTarget(data)} subject={data.id} />;
}

const TEXT_FILTER_DEF = {
  filter: 'agTextColumnFilter' as const,
  filterParams: {
    filterOptions: ['contains' as const],
    maxNumConditions: 1,
  },
};

export function StatusCellRenderer({
  value,
  data,
}: {
  value: SampleStatus;
  data: SampleListItem;
}) {
  // An in-flight provisional row has no true status yet: `status` is derived
  // from (error_message, limit), both unset while running, so it reads
  // "success".
  if (data?.is_provisional) {
    // Green is current activity, as for a running job.
    if (!data.completed_at) {
      return (
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap font-medium text-ok">
          <i className="dot dot-live text-ok-mark" aria-hidden="true" />
          Running
        </span>
      );
    }
    const pending = STATUS_OPTIONS.find(o => o.value === value)?.label || value;
    return (
      <span
        className="inline-flex items-center gap-1.5 whitespace-nowrap font-medium text-muted"
        title="Completed; awaiting the final import"
      >
        <i className="dot text-neutral-mark" aria-hidden="true" />
        {`${pending} (pending)`}
      </span>
    );
  }

  // Same language as the Jobs list: a dot and a label, neutral for success.
  const [textClass, dotClass] =
    value === 'success'
      ? ['text-ink-2', 'text-neutral-mark']
      : value === 'error'
        ? ['text-err font-medium', 'text-err-mark']
        : ['text-warn font-medium', 'text-warn-mark'];

  const label = STATUS_OPTIONS.find(o => o.value === value)?.label || value;
  const title =
    value === 'error' && data?.error_message ? data.error_message : undefined;

  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap ${textClass}`}
      title={title}
    >
      <i className={`dot ${dotClass}`} aria-hidden="true" />
      {label}
    </span>
  );
}

function ScoreCellRenderer({ data }: { data?: SampleListItem }) {
  const scores = data?.scores ?? [];
  // The cell caps at three entries and clips long dict values; the tooltip
  // must not, or the hover reveals nothing the cell didn't already show.
  return <span title={formatScoresFull(scores)}>{formatScores(scores)}</span>;
}

function ErrorCellRenderer({ value }: { value: string | null }) {
  if (!value) return <span>-</span>;
  const preview = value.length > 100 ? value.slice(0, 100) + '...' : value;
  return (
    <span className="copy-button-cell">
      <span className="copy-button-cell-text text-err" title={value}>
        {preview}
      </span>
      <CopyButton value={value} />
    </span>
  );
}

export function SampleList() {
  const { apiFetch } = useApiFetch();
  const [fetchError, setFetchError] = useState<Error | null>(null);
  const gridRef = useRef<AgGridReact<SampleListItem>>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL params
  const [searchQuery, setSearchQuery] = useState(
    () => searchParams.get('search') || ''
  );
  const [statusFilter, setStatusFilter] = useState<SampleStatus | ''>(
    () => (searchParams.get('status') as SampleStatus) || ''
  );
  const [scoreMin, setScoreMin] = useState<string>(
    () => searchParams.get('score_min') || ''
  );
  const [scoreMax, setScoreMax] = useState<string>(
    () => searchParams.get('score_max') || ''
  );
  const [completedAfter, setCompletedAfter] = useState<string>(
    () => searchParams.get('completed_after') || ''
  );
  const [completedBefore, setCompletedBefore] = useState<string>(
    () => searchParams.get('completed_before') || ''
  );
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [columnFilterValues, setColumnFilterValues] = useState<
    Record<string, string>
  >(() => {
    const initial: Record<string, string> = {};
    for (const [, paramName] of Object.entries(COLUMN_FILTER_PARAMS)) {
      const value = searchParams.get(paramName);
      if (value) initial[paramName] = value;
    }
    return initial;
  });
  const { getAbortController } = useAbortController();

  // Sync URL with filter state
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery.trim()) params.set('search', searchQuery.trim());
    if (statusFilter) params.set('status', statusFilter);
    if (scoreMin) params.set('score_min', scoreMin);
    if (scoreMax) params.set('score_max', scoreMax);
    if (completedAfter) params.set('completed_after', completedAfter);
    if (completedBefore) params.set('completed_before', completedBefore);
    for (const [paramName, value] of Object.entries(columnFilterValues)) {
      params.set(paramName, value);
    }
    setSearchParams(params, { replace: true });
  }, [
    searchQuery,
    statusFilter,
    scoreMin,
    scoreMax,
    completedAfter,
    completedBefore,
    columnFilterValues,
    setSearchParams,
  ]);

  // Create datasource for infinite row model
  const datasource = useMemo<IDatasource>(() => {
    return {
      rowCount: undefined,
      getRows: async (params: IGetRowsParams) => {
        const abortController = getAbortController();
        setIsLoading(true);
        setFetchError(null);

        const page = Math.floor(params.startRow / PAGE_SIZE) + 1;

        const queryParams = new URLSearchParams({
          page: page.toString(),
          limit: PAGE_SIZE.toString(),
        });

        if (searchQuery.trim()) {
          queryParams.set('search', searchQuery.trim());
        }

        if (statusFilter) {
          queryParams.append('status', statusFilter);
        }

        const scoreMinNum = parseFloat(scoreMin);
        const scoreMaxNum = parseFloat(scoreMax);
        if (!isNaN(scoreMinNum)) {
          queryParams.set('score_min', scoreMinNum.toString());
        }
        if (!isNaN(scoreMaxNum)) {
          queryParams.set('score_max', scoreMaxNum.toString());
        }

        // completedAfter/completedBefore are already absolute UTC ISO instants.
        if (completedAfter) {
          queryParams.set('completed_after', completedAfter);
        }
        if (completedBefore) {
          queryParams.set('completed_before', completedBefore);
        }

        // Handle column filters from AG Grid filter model
        const filterModel = params.filterModel;
        if (filterModel) {
          for (const [field, paramName] of Object.entries(
            COLUMN_FILTER_PARAMS
          )) {
            const value = filterModel[field]?.filter;
            if (value) {
              queryParams.set(paramName, value);
            }
          }
        }

        // Handle sorting
        const sortModel = params.sortModel;
        if (sortModel && sortModel.length > 0) {
          queryParams.set('sort_by', sortModel[0].colId);
          queryParams.set('sort_order', sortModel[0].sort ?? 'desc');
        }

        try {
          const response = await apiFetch(
            `/meta/samples?${queryParams}`,
            {
              signal: abortController.signal,
            },
            { returnNonOkResponse: true }
          );

          // If this request was aborted, don't process the response
          if (abortController.signal.aborted) {
            // Superseded by a newer query: release the grid's block loader
            // (maxConcurrentDatasourceRequests) or nothing loads again.
            params.failCallback();
            return;
          }

          if (!response) throw new Error('Could not load samples');
          if (!response.ok)
            throw new Error(`Could not load samples (HTTP ${response.status})`);

          const data = await response.json();
          if (abortController.signal.aborted) {
            params.failCallback();
            return;
          }
          // For infinite model, lastRow tells the grid when we've reached the end.
          // Use has_more to determine if there are additional pages.
          const lastRow = data.has_more
            ? -1
            : params.startRow + data.items.length;
          params.successCallback(data.items, lastRow);
          setIsLoading(false);
          setHasLoaded(true);
        } catch (error) {
          // Don't update state if request was aborted
          if (abortController.signal.aborted) {
            // Superseded by a newer query: release the grid's block loader
            // (maxConcurrentDatasourceRequests) or nothing loads again.
            params.failCallback();
            return;
          }
          console.error('Sample list fetch failed:', error);
          setFetchError(
            error instanceof Error ? error : new Error(String(error))
          );
          setIsLoading(false);
          params.failCallback();
        }
      },
    };
  }, [
    apiFetch,
    searchQuery,
    statusFilter,
    scoreMin,
    scoreMax,
    completedAfter,
    completedBefore,
    getAbortController,
  ]);

  // Keep AG Grid's datasource identity stable so toolbar filters can refresh
  // existing rows instead of replacing them with empty infinite-grid rows.
  const datasourceRef = useRef(datasource);
  const gridDatasource = useMemo<IDatasource>(
    () => ({
      getRows: params => datasourceRef.current.getRows(params),
    }),
    []
  );

  const { primaryUrl, viewers } = useViewers();
  const hasAlternateViewers = viewers.some(
    viewer => viewer.kind !== 'builtin' && viewer.url_templates.sample !== null
  );

  // Column definitions
  const columnDefs = useMemo<ColDef<SampleListItem>[]>(
    () => [
      {
        field: 'id',
        headerName: 'Sample',
        width: 120,
        pinned: 'left',
        ...TEXT_FILTER_DEF,
      },
      {
        field: 'task_name',
        headerName: 'Task',
        width: 190,
        ...TEXT_FILTER_DEF,
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 130,
        cellRenderer: StatusCellRenderer,
      },
      {
        // field stays score_value: the grid sends colId as sort_by (see below),
        // so renaming it would break server-side sorting. The renderer reads
        // data.scores instead of the field value.
        field: 'score_value',
        headerName: 'Score',
        width: 180,
        cellRenderer: ScoreCellRenderer,
      },
      {
        field: 'model',
        headerName: 'Model',
        width: 180,
        ...TEXT_FILTER_DEF,
      },
      {
        field: 'eval_set_id',
        headerName: 'Eval Set',
        width: 260,
        cellRenderer: CopyButtonCellRenderer,
        ...TEXT_FILTER_DEF,
      },
      {
        field: 'created_by',
        headerName: 'Author',
        width: 120,
        valueFormatter: params => params.value || '-',
        ...TEXT_FILTER_DEF,
      },
      {
        field: 'task_version',
        headerName: 'Task Version',
        width: 170,
        valueFormatter: params => params.value || '-',
        ...TEXT_FILTER_DEF,
      },
      {
        field: 'input_tokens',
        headerName: 'In Tokens',
        width: 90,
        cellRenderer: NumberCellRenderer,
      },
      {
        field: 'output_tokens',
        headerName: 'Out Tokens',
        width: 95,
        cellRenderer: NumberCellRenderer,
      },
      {
        field: 'total_tokens',
        headerName: 'Total Tokens',
        width: 100,
        cellRenderer: NumberCellRenderer,
      },
      {
        field: 'message_count',
        headerName: 'Messages',
        width: 90,
        cellRenderer: NumberCellRenderer,
      },
      {
        field: 'action_count',
        headerName: 'Actions',
        width: 80,
        cellRenderer: NumberCellRenderer,
      },
      {
        field: 'total_time_seconds',
        headerName: 'Duration',
        width: 90,
        cellRenderer: DurationCellRenderer,
      },
      {
        colId: 'open',
        headerName: '',
        width: 80,
        sortable: false,
        filter: false,
        hide: true,
        cellRenderer: OpenInCellRenderer,
      },
      {
        colId: 'timeline',
        headerName: 'Timeline',
        width: 90,
        cellRenderer: TimelineLinkCellRenderer,
        sortable: false,
        filter: false,
      },
      {
        field: 'completed_at',
        headerName: 'Completed',
        width: 110,
        cellRenderer: TimeAgoCellRenderer,
        sort: 'desc',
      },
      {
        field: 'uuid',
        headerName: 'UUID',
        width: 290,
        cellRenderer: CopyButtonCellRenderer,
      },
      {
        field: 'eval_id',
        headerName: 'Eval ID',
        width: 200,
        hide: true,
      },
      {
        field: 'reasoning_tokens',
        headerName: 'Reasoning Tokens',
        width: 130,
        cellRenderer: NumberCellRenderer,
        hide: true,
      },
      {
        field: 'working_time_seconds',
        headerName: 'Working Time',
        width: 110,
        cellRenderer: DurationCellRenderer,
        hide: true,
      },
      {
        field: 'generation_time_seconds',
        headerName: 'Gen Time',
        width: 90,
        cellRenderer: DurationCellRenderer,
        hide: true,
      },
      {
        field: 'error_message',
        headerName: 'Error',
        width: 300,
        cellRenderer: ErrorCellRenderer,
        ...TEXT_FILTER_DEF,
      },
      {
        field: 'is_invalid',
        headerName: 'Invalid',
        width: 70,
      },
      {
        field: 'score_scorer',
        headerName: 'Scorer',
        width: 100,
        hide: true,
      },
      {
        field: 'location',
        headerName: 'Location',
        width: 300,
        hide: true,
      },
    ],
    []
  );

  const defaultColDef = useMemo<ColDef<SampleListItem>>(
    () => ({
      sortable: true,
      resizable: true,
      filter: false,
      minWidth: 80,
    }),
    []
  );

  const getRowId = useCallback(
    (params: GetRowIdParams<SampleListItem>) => params.data.uuid,
    []
  );

  const handleRowClicked = useCallback(
    (event: RowClickedEvent<SampleListItem>) => {
      if (
        isCopyButtonClick(event) ||
        isLinkClick(event) ||
        isButtonClick(event)
      ) {
        return;
      }
      const mouseEvent = event.event as MouseEvent | undefined;
      if (
        mouseEvent?.button === 1 ||
        mouseEvent?.ctrlKey ||
        mouseEvent?.metaKey
      ) {
        return;
      }
      if (!event.data) return;
      openViewerUrl(primaryUrl(sampleTarget(event.data)));
    },
    [primaryUrl]
  );

  const handleCellMouseDown = useCallback(
    (event: CellMouseDownEvent<SampleListItem>) => {
      if (
        isCopyButtonClick(event) ||
        isLinkClick(event) ||
        isButtonClick(event)
      ) {
        return;
      }
      const mouseEvent = event.event as MouseEvent;
      if (mouseEvent.button === 1 || mouseEvent.ctrlKey || mouseEvent.metaKey) {
        if (!event.data) return;
        openViewerUrl(primaryUrl(sampleTarget(event.data)), { newTab: true });
      }
    },
    [primaryUrl]
  );

  const onFilterChanged = useCallback(
    (_event: FilterChangedEvent<SampleListItem>) => {
      const model = gridRef.current?.api?.getFilterModel();
      const newValues: Record<string, string> = {};
      if (model) {
        for (const [field, paramName] of Object.entries(COLUMN_FILTER_PARAMS)) {
          const value = model[field]?.filter;
          if (value) newValues[paramName] = value;
        }
      }
      setColumnFilterValues(newValues);
    },
    []
  );

  const appColumns = useMemo(
    () => ({ open: hasAlternateViewers }),
    [hasAlternateViewers]
  );
  const layout = useGridLayout('samples', appColumns);
  // Destructured so the callback below depends on the hook's stable handler
  // rather than on the object the hook returns each render.
  const { onGridReady: onLayoutGridReady } = layout.gridProps;

  const onGridReady = useCallback(
    (params: GridReadyEvent<SampleListItem>) => {
      // This grid has its own onGridReady (filters + datasource), so the
      // layout hook's handler is chained rather than spread.
      onLayoutGridReady(params);
      // Restore column filters from URL params
      const initialFilterModel: Record<
        string,
        { type: string; filter: string }
      > = {};
      for (const [field, paramName] of Object.entries(COLUMN_FILTER_PARAMS)) {
        const value = searchParams.get(paramName);
        if (value) {
          initialFilterModel[field] = { type: 'contains', filter: value };
        }
      }
      if (Object.keys(initialFilterModel).length > 0) {
        params.api.setFilterModel(initialFilterModel);
      }
      params.api.setGridOption('datasource', gridDatasource);
    },
    [gridDatasource, searchParams, onLayoutGridReady]
  );

  // A new query starts at page one. Drop later cached blocks, retain the
  // current first page during the request, and let its response set the count.
  useEffect(() => {
    if (datasourceRef.current === datasource) return;
    datasourceRef.current = datasource;
    const api = gridRef.current?.api;
    if (api) {
      getAbortController();
      api.setRowCount(
        // An unknown count at an exact block boundary adds a row, keeping
        // the next cached block alive. End inside block zero until it reloads.
        Math.max(1, Math.min(PAGE_SIZE - 1, api.getDisplayedRowCount())),
        false
      );
      api.ensureIndexVisible(0, 'top');
      api.refreshInfiniteCache();
    }
  }, [datasource, getAbortController]);

  const handleStatusChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setStatusFilter(e.target.value as SampleStatus | '');
    },
    []
  );

  const clearFilters = useCallback(() => {
    setSearchQuery('');
    setStatusFilter('');
    setScoreMin('');
    setScoreMax('');
    setCompletedAfter('');
    setCompletedBefore('');
    gridRef.current?.api?.setFilterModel(null);
    setColumnFilterValues({});
  }, []);

  const handleRefresh = useCallback(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.refreshInfiniteCache();
    }
  }, []);

  const hasColumnFilters = Object.keys(columnFilterValues).length > 0;
  const hasFilters =
    searchQuery ||
    statusFilter ||
    scoreMin ||
    scoreMax ||
    completedAfter ||
    completedBefore ||
    hasColumnFilters;

  if (fetchError && !hasLoaded) {
    return <ErrorDisplay message={fetchError.toString()} />;
  }

  return (
    <div className="pane flex h-full flex-col overflow-hidden">
      <PageHeader
        title="Samples"
        description="Every sample in the warehouse. Filter by score, completion time or any column, then open one to read its transcript."
        actions={
          <>
            {hasFilters && (
              <button onClick={clearFilters} className="btn btn-ghost">
                Clear filters
              </button>
            )}
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="btn btn-ghost"
              title="Refresh results"
            >
              Refresh
            </button>
          </>
        }
      >
        <div className="relative min-w-40 max-w-sm flex-1">
          <input
            type="search"
            aria-label="Search samples"
            placeholder="Search samples…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="input h-7 w-full"
          />
          <LoadingIndicator loading={isLoading} />
        </div>
        <select
          id="status-filter"
          aria-label="Status filter"
          value={statusFilter}
          onChange={handleStatusChange}
          className="input h-7 shrink-0 px-2"
        >
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </PageHeader>
      {/* Range filters wrap onto further lines at laptop widths rather than
          scrolling sideways; each labelled pair stays together. */}
      <div className="subbar">
        <div
          className="flex shrink-0 items-center gap-1.5"
          title="Filters on a sample's single final score. Samples with several final scores are excluded, as are samples still running."
        >
          <span className="subbar-label">Score</span>
          <input
            type="number"
            step="0.01"
            aria-label="Minimum score"
            placeholder="min"
            value={scoreMin}
            onChange={e => setScoreMin(e.target.value)}
            className="input h-7 w-16 px-2"
          />
          <span className="text-muted">–</span>
          <input
            type="number"
            step="0.01"
            aria-label="Maximum score"
            placeholder="max"
            value={scoreMax}
            onChange={e => setScoreMax(e.target.value)}
            className="input h-7 w-16 px-2"
          />
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <span className="subbar-label">Completed</span>
          <input
            type="datetime-local"
            aria-label="Completed after"
            title="Completed after"
            value={isoToLocalInput(completedAfter)}
            max={isoToLocalInput(completedBefore) || undefined}
            onChange={e =>
              setCompletedAfter(localInputToIso(e.target.value) ?? '')
            }
            className="input h-7 px-2 text-xs"
          />
          <span className="text-muted">–</span>
          <input
            type="datetime-local"
            aria-label="Completed before"
            title="Completed before"
            value={isoToLocalInput(completedBefore)}
            min={isoToLocalInput(completedAfter) || undefined}
            onChange={e =>
              setCompletedBefore(localInputToIso(e.target.value) ?? '')
            }
            className="input h-7 px-2 text-xs"
          />
        </div>
      </div>

      {fetchError && (
        <RefreshError error={fetchError} onRetry={handleRefresh} />
      )}
      <div className="flex-1 overflow-hidden relative">
        {!hasLoaded && (
          <div className="absolute inset-0 z-10 bg-surface">
            {/* Keep empty infinite-grid rows covered, but only reveal the
                skeleton if the initial request takes longer than 150ms. */}
            <ListSkeleton
              columns={[
                'w-40',
                'w-32',
                'w-16',
                'w-36',
                'w-24',
                'w-20',
                'w-16',
                'flex-1',
              ]}
            />
          </div>
        )}
        <div className="ag-theme-quartz rows-open relative h-full w-full">
          <ResetGridLayoutButton layout={layout} />
          <AgGridReact<SampleListItem>
            {...layout.gridProps}
            ref={gridRef}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            rowModelType="infinite"
            onGridReady={onGridReady}
            onFilterChanged={onFilterChanged}
            onRowClicked={handleRowClicked}
            onCellMouseDown={handleCellMouseDown}
            cacheBlockSize={PAGE_SIZE}
            cacheOverflowSize={2}
            maxConcurrentDatasourceRequests={1}
            infiniteInitialRowCount={PAGE_SIZE}
            maxBlocksInCache={10}
            getRowId={getRowId}
            animateRows={false}
            suppressCellFocus={true}
          />
        </div>
      </div>
    </div>
  );
}
